import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from "react";

import {
  createShopApiGraphqlClient,
  type ShopApiGraphqlClient,
} from "../../../packages/pvc-product/shop-api-transport";
import { AccountChrome } from "./site-chrome";

export const ACCOUNT_SECURITY_PATH = "/account/security";
export const PASSWORD_RESET_REQUEST_PATH = "/account/password-reset";
export const PASSWORD_RESET_CONFIRM_PATH = "/account/password-reset/confirm";
export const EMAIL_CHANGE_CONFIRM_PATH = "/account/email-change/confirm";

class AccountSecurityError extends Error {
  constructor(public readonly errorCode: string, message: string) {
    super(message);
    this.name = "AccountSecurityError";
  }
}

type SecurityIdentity = {
  firstName: string;
  emailAddress: string;
};

const ACTIVE_CUSTOMER_QUERY = `
  query HappyPrintSecurityIdentity {
    activeCustomer { firstName emailAddress }
  }
`;

const UPDATE_PASSWORD_MUTATION = `
  mutation HappyPrintUpdateCustomerPassword($currentPassword: String!, $newPassword: String!) {
    updateCustomerPassword(currentPassword: $currentPassword, newPassword: $newPassword) {
      __typename
      ... on Success { success }
      ... on ErrorResult { errorCode message }
    }
  }
`;

const REQUEST_PASSWORD_RESET_MUTATION = `
  mutation HappyPrintRequestPasswordReset($emailAddress: String!) {
    requestPasswordReset(emailAddress: $emailAddress) {
      __typename
      ... on Success { success }
      ... on ErrorResult { errorCode message }
    }
  }
`;

const RESET_PASSWORD_MUTATION = `
  mutation HappyPrintResetPassword($token: String!, $password: String!) {
    resetPassword(token: $token, password: $password) {
      __typename
      ... on CurrentUser { id identifier }
      ... on ErrorResult { errorCode message }
    }
  }
`;

const REQUEST_EMAIL_CHANGE_MUTATION = `
  mutation HappyPrintRequestEmailChange($password: String!, $newEmailAddress: String!) {
    requestUpdateCustomerEmailAddress(password: $password, newEmailAddress: $newEmailAddress) {
      __typename
      ... on Success { success }
      ... on ErrorResult { errorCode message }
    }
  }
`;

const CONFIRM_EMAIL_CHANGE_MUTATION = `
  mutation HappyPrintConfirmEmailChange($token: String!) {
    updateCustomerEmailAddress(token: $token) {
      __typename
      ... on Success { success }
      ... on ErrorResult { errorCode message }
    }
  }
`;

function object(value: unknown, path: string): Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function string(value: unknown, path: string): string {
  if (typeof value !== "string" || !value) throw new Error(`${path} must be a non-empty string`);
  return value;
}

function decodeResult(raw: unknown, field: string): void {
  const data = object(raw, "data");
  const result = object(data[field], `data.${field}`);
  if (result.__typename === "Success" || result.__typename === "CurrentUser") return;
  if (typeof result.errorCode === "string" && typeof result.message === "string") {
    throw new AccountSecurityError(result.errorCode, result.message);
  }
  throw new Error(`data.${field} returned an unexpected result`);
}

function createAccountSecurityApi(client: ShopApiGraphqlClient) {
  return {
    async identity(signal?: AbortSignal): Promise<SecurityIdentity | null> {
      const raw = await client.execute({ operationName: "HappyPrintSecurityIdentity", document: ACTIVE_CUSTOMER_QUERY, signal });
      const data = object(raw, "data");
      if (data.activeCustomer == null) return null;
      const customer = object(data.activeCustomer, "data.activeCustomer");
      return {
        firstName: string(customer.firstName, "data.activeCustomer.firstName"),
        emailAddress: string(customer.emailAddress, "data.activeCustomer.emailAddress"),
      };
    },

    async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
      const raw = await client.execute({
        operationName: "HappyPrintUpdateCustomerPassword",
        document: UPDATE_PASSWORD_MUTATION,
        variables: { currentPassword, newPassword },
      });
      decodeResult(raw, "updateCustomerPassword");
    },

    async requestPasswordReset(emailAddress: string): Promise<void> {
      const raw = await client.execute({
        operationName: "HappyPrintRequestPasswordReset",
        document: REQUEST_PASSWORD_RESET_MUTATION,
        variables: { emailAddress },
      });
      decodeResult(raw, "requestPasswordReset");
    },

    async resetPassword(token: string, password: string): Promise<void> {
      const raw = await client.execute({
        operationName: "HappyPrintResetPassword",
        document: RESET_PASSWORD_MUTATION,
        variables: { token, password },
      });
      decodeResult(raw, "resetPassword");
    },

    async requestEmailChange(password: string, newEmailAddress: string): Promise<void> {
      const raw = await client.execute({
        operationName: "HappyPrintRequestEmailChange",
        document: REQUEST_EMAIL_CHANGE_MUTATION,
        variables: { password, newEmailAddress },
      });
      decodeResult(raw, "requestUpdateCustomerEmailAddress");
    },

    async confirmEmailChange(token: string): Promise<void> {
      const raw = await client.execute({
        operationName: "HappyPrintConfirmEmailChange",
        document: CONFIRM_EMAIL_CHANGE_MUTATION,
        variables: { token },
      });
      decodeResult(raw, "updateCustomerEmailAddress");
    },
  };
}

function createBrowserSecurityApi() {
  const endpoint = import.meta.env.VITE_SHOP_API_URL || "/shop-api";
  return createAccountSecurityApi(createShopApiGraphqlClient({ endpoint }));
}

function normalizePath(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

function errorText(error: unknown): string {
  if (error instanceof AccountSecurityError) {
    switch (error.errorCode) {
      case "INVALID_CREDENTIALS_ERROR": return "הסיסמה הנוכחית אינה נכונה.";
      case "PASSWORD_VALIDATION_ERROR": return "הסיסמה החדשה אינה עומדת בדרישות האבטחה.";
      case "EMAIL_ADDRESS_CONFLICT_ERROR": return "כתובת האימייל הזו כבר משויכת לחשבון אחר.";
      case "PASSWORD_RESET_TOKEN_INVALID_ERROR": return "קישור איפוס הסיסמה אינו תקין.";
      case "PASSWORD_RESET_TOKEN_EXPIRED_ERROR": return "קישור איפוס הסיסמה פג תוקף. יש לבקש קישור חדש.";
      case "IDENTIFIER_CHANGE_TOKEN_INVALID_ERROR": return "קישור שינוי האימייל אינו תקין.";
      case "IDENTIFIER_CHANGE_TOKEN_EXPIRED_ERROR": return "קישור שינוי האימייל פג תוקף. יש לבקש שינוי מחדש.";
      default: return error.message || "לא הצלחנו להשלים את הפעולה.";
    }
  }
  return "אירעה תקלה בתקשורת עם השרת. אפשר לנסות שוב.";
}

function PasswordResetRequestPage(): ReactElement {
  const api = useMemo(() => createBrowserSecurityApi(), []);
  const [emailAddress, setEmailAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.requestPasswordReset(emailAddress.trim());
      setSent(true);
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AccountChrome>
      <main className="account-auth-page">
        <section className="account-auth-card">
          <div className="account-auth-heading"><span className="account-kicker">אבטחת החשבון</span><h1>איפוס סיסמה</h1><p>נשלח קישור מאובטח לכתובת האימייל של החשבון.</p></div>
          {sent ? (
            <div className="account-success" role="status">
              אם הכתובת שייכת לחשבון קיים, נשלח אליה קישור לאיפוס הסיסמה. הקישור מוגבל בזמן.
            </div>
          ) : (
            <form className="account-form" onSubmit={event => void submit(event)}>
              <label><span>אימייל</span><input required type="email" autoComplete="email" value={emailAddress} onChange={event => setEmailAddress(event.target.value)} /></label>
              {error ? <div className="account-error" role="alert">{error}</div> : null}
              <button className="account-primary-button" type="submit" disabled={busy}>{busy ? "שולח…" : "שליחת קישור לאיפוס"}</button>
            </form>
          )}
          <p className="account-auth-switch"><a href="/login">חזרה לכניסה</a></p>
        </section>
      </main>
    </AccountChrome>
  );
}

function PasswordResetConfirmPage(): ReactElement {
  const api = useMemo(() => createBrowserSecurityApi(), []);
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(token ? null : "קישור איפוס הסיסמה אינו תקין.");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!token || busy) return;
    if (password.length < 8) {
      setError("יש לבחור סיסמה באורך 8 תווים לפחות.");
      return;
    }
    if (password !== confirmPassword) {
      setError("שתי הסיסמאות אינן זהות.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AccountChrome>
      <main className="account-auth-page">
        <section className="account-auth-card">
          <div className="account-auth-heading"><span className="account-kicker">אבטחת החשבון</span><h1>בחירת סיסמה חדשה</h1><p>הסיסמה החדשה תחליף את הסיסמה הקודמת בחשבון.</p></div>
          {done ? (
            <><div className="account-success" role="status">הסיסמה עודכנה בהצלחה.</div><a className="account-primary-button account-button-link" href="/account">מעבר לאזור האישי</a></>
          ) : (
            <form className="account-form" onSubmit={event => void submit(event)}>
              <label><span>סיסמה חדשה</span><input required minLength={8} type="password" autoComplete="new-password" value={password} onChange={event => setPassword(event.target.value)} /></label>
              <label><span>אימות סיסמה חדשה</span><input required minLength={8} type="password" autoComplete="new-password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} /></label>
              {error ? <div className="account-error" role="alert">{error}</div> : null}
              <button className="account-primary-button" type="submit" disabled={busy || !token}>{busy ? "מעדכן…" : "עדכון סיסמה"}</button>
            </form>
          )}
        </section>
      </main>
    </AccountChrome>
  );
}

function EmailChangeConfirmPage(): ReactElement {
  const api = useMemo(() => createBrowserSecurityApi(), []);
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [status, setStatus] = useState<"loading" | "success" | "error">(token ? "loading" : "error");
  const [message, setMessage] = useState(token ? "מאמתים את כתובת האימייל החדשה…" : "קישור שינוי האימייל אינו תקין.");

  useEffect(() => {
    if (!token) return;
    void api.confirmEmailChange(token).then(() => {
      setStatus("success");
      setMessage("כתובת האימייל עודכנה בהצלחה.");
    }).catch(cause => {
      setStatus("error");
      setMessage(errorText(cause));
    });
  }, [api, token]);

  return (
    <AccountChrome>
      <main className="account-auth-page">
        <section className="account-auth-card">
          <div className="account-auth-heading"><span className="account-kicker">אבטחת החשבון</span><h1>אימות אימייל חדש</h1><p>השינוי ייכנס לתוקף רק לאחר אימות הקישור.</p></div>
          <div className={status === "error" ? "account-error" : "account-success"} role="status">{message}</div>
          {status === "success" ? <a className="account-primary-button account-button-link" href="/account/settings">חזרה לפרטי החשבון</a> : null}
        </section>
      </main>
    </AccountChrome>
  );
}

function AccountSecurityPage(): ReactElement {
  const api = useMemo(() => createBrowserSecurityApi(), []);
  const [identity, setIdentity] = useState<SecurityIdentity | null | undefined>(undefined);
  const [loadError, setLoadError] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [newEmailAddress, setNewEmailAddress] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMessage, setEmailMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void api.identity(controller.signal).then(setIdentity).catch(() => setLoadError(true));
    return () => controller.abort();
  }, [api]);

  const changePassword = async (event: FormEvent) => {
    event.preventDefault();
    if (passwordBusy) return;
    if (newPassword.length < 8) {
      setError("יש לבחור סיסמה חדשה באורך 8 תווים לפחות.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("שתי הסיסמאות החדשות אינן זהות.");
      return;
    }
    setPasswordBusy(true);
    setPasswordMessage(null);
    setError(null);
    try {
      await api.updatePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMessage("הסיסמה עודכנה בהצלחה.");
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setPasswordBusy(false);
    }
  };

  const changeEmail = async (event: FormEvent) => {
    event.preventDefault();
    if (!identity || emailBusy) return;
    const next = newEmailAddress.trim().toLowerCase();
    if (!next || next === identity.emailAddress.toLowerCase()) {
      setError("יש להזין כתובת אימייל חדשה ושונה מהכתובת הנוכחית.");
      return;
    }
    setEmailBusy(true);
    setEmailMessage(null);
    setError(null);
    try {
      await api.requestEmailChange(emailPassword, next);
      setNewEmailAddress("");
      setEmailPassword("");
      setEmailMessage(`שלחנו קישור אימות ל־${next}. כתובת האימייל תשתנה רק לאחר פתיחת הקישור.`);
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setEmailBusy(false);
    }
  };

  if (identity === undefined && !loadError) {
    return <AccountChrome><main className="account-status-page">טוענים הגדרות אבטחה…</main></AccountChrome>;
  }
  if (!identity) {
    return (
      <AccountChrome>
        <main className="account-status-page"><section><h1>צריך להתחבר לחשבון</h1><p>{loadError ? "לא הצלחנו לטעון את החשבון כרגע." : "כדי לשנות סיסמה או אימייל יש להתחבר."}</p><a className="account-primary-button account-button-link" href="/login">לכניסה</a></section></main>
      </AccountChrome>
    );
  }

  return (
    <AccountChrome>
      <main className="account-page account-security-page">
        <div className="account-security-heading">
          <div><span className="account-kicker">האזור האישי</span><h1>אבטחה וכניסה</h1><p>שינוי סיסמה וכתובת האימייל של החשבון.</p></div>
          <a className="account-secondary-button" href="/account/settings">חזרה לפרטים וכתובות</a>
        </div>
        {error ? <div className="account-error" role="alert">{error}</div> : null}
        <div className="account-security-grid">
          <section className="account-panel account-security-card">
            <div><span className="account-kicker">סיסמה</span><h2>שינוי סיסמה</h2><p>נדרש להזין את הסיסמה הנוכחית לפני בחירת סיסמה חדשה.</p></div>
            <form className="account-form" onSubmit={event => void changePassword(event)}>
              <label><span>סיסמה נוכחית</span><input required type="password" autoComplete="current-password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} /></label>
              <label><span>סיסמה חדשה</span><input required minLength={8} type="password" autoComplete="new-password" value={newPassword} onChange={event => setNewPassword(event.target.value)} /></label>
              <label><span>אימות סיסמה חדשה</span><input required minLength={8} type="password" autoComplete="new-password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} /></label>
              {passwordMessage ? <div className="account-success" role="status">{passwordMessage}</div> : null}
              <button className="account-primary-button" type="submit" disabled={passwordBusy}>{passwordBusy ? "מעדכן…" : "שינוי סיסמה"}</button>
            </form>
          </section>
          <section className="account-panel account-security-card">
            <div><span className="account-kicker">אימייל</span><h2>שינוי כתובת אימייל</h2><p>האימייל הנוכחי: <bdi>{identity.emailAddress}</bdi>. שינוי דורש את הסיסמה הנוכחית ואימות מהאימייל החדש.</p></div>
            <form className="account-form" onSubmit={event => void changeEmail(event)}>
              <label><span>אימייל חדש</span><input required type="email" autoComplete="email" value={newEmailAddress} onChange={event => setNewEmailAddress(event.target.value)} /></label>
              <label><span>סיסמה נוכחית</span><input required type="password" autoComplete="current-password" value={emailPassword} onChange={event => setEmailPassword(event.target.value)} /></label>
              {emailMessage ? <div className="account-success" role="status">{emailMessage}</div> : null}
              <button className="account-primary-button" type="submit" disabled={emailBusy}>{emailBusy ? "שולח אימות…" : "שליחת קישור אימות"}</button>
            </form>
          </section>
        </div>
      </main>
    </AccountChrome>
  );
}

export function AccountSecurityRouter(): ReactElement {
  const path = normalizePath(window.location.pathname);
  if (path === PASSWORD_RESET_REQUEST_PATH) return <PasswordResetRequestPage />;
  if (path === PASSWORD_RESET_CONFIRM_PATH) return <PasswordResetConfirmPage />;
  if (path === EMAIL_CHANGE_CONFIRM_PATH) return <EmailChangeConfirmPage />;
  return <AccountSecurityPage />;
}

export function isAccountSecurityPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path === ACCOUNT_SECURITY_PATH || path === PASSWORD_RESET_REQUEST_PATH || path === PASSWORD_RESET_CONFIRM_PATH || path === EMAIL_CHANGE_CONFIRM_PATH;
}

export function AccountSecurityLauncher(): ReactElement | null {
  const path = normalizePath(window.location.pathname);
  if (path === "/login") {
    return <a className="account-password-reset-launcher" href={PASSWORD_RESET_REQUEST_PATH}>שכחת סיסמה?</a>;
  }
  if (path === "/account" || path === "/account/settings") {
    return <a className="account-security-launcher" href={ACCOUNT_SECURITY_PATH}>אבטחה וסיסמה</a>;
  }
  return null;
}
